// src/pages/VehicleOwnerSignUp.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useUser, ROLES } from "../context/UserContext";
import {
  ownerLocationAutocompleteApi,
  ownerRequestOtpApi,
  ownerCompleteProfileApi,
} from "../api/ownerPortalApi";

/* ───────── Inline icon helper ───────── */
const Icon = ({ d, size = 20, title, className }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    role={title ? "img" : "presentation"}
    aria-label={title}
    className={className}
  >
    {title ? <title>{title}</title> : null}
    <path d={d} fill="currentColor" />
  </svg>
);

const mdi = {
  steering:
    "M12 2A10 10 0 0 0 2 12C2 17.5 6.5 22 12 22S22 17.5 22 12A10 10 0 0 0 12 2M12 4A8 8 0 0 1 20 12C20 12.35 19.97 12.69 19.92 13H15.83C15.46 11.82 14.35 11 13.11 11H10.89C9.65 11 8.54 11.82 8.17 13H4.08C4.03 12.69 4 12.35 4 12A8 8 0 0 1 12 4M8.5 15A1.5 1.5 0 0 1 10 16.5A1.5 1.5 0 0 1 8.5 18A1.5 1.5 0 0 1 7 16.5A1.5 1.5 0 0 1 8.5 15M15.5 15A1.5 1.5 0 0 1 17 16.5A1.5 1.5 0 0 1 15.5 18A1.5 1.5 0 0 1 14 16.5A1.5 1.5 0 0 1 15.5 15M12 12.5A1.5 1.5 0 0 1 13.5 14A1.5 1.5 0 0 1 12 15.5A1.5 1.5 0 0 1 10.5 14A1.5 1.5 0 0 1 12 12.5Z",
  emailOutline:
    "M20 4H4C2.9 4 2 4.9 2 6V18C2 19.11 2.9 20 4 20H20C21.11 20 22 19.11 22 18V6C22 4.9 21.11 4 20 4M20 8L12 13L4 8V6L12 11L20 6",
  accountOutline:
    "M12 4A4 4 0 0 1 16 8A4 4 0 0 1 12 12A4 4 0 0 1 8 8A4 4 0 0 1 12 4M12 14C16.42 14 20 15.79 20 18V20H4V18C4 15.79 7.58 14 12 14Z",
  phoneOutline:
    "M6.62 10.79C8.06 13.62 10.38 15.94 13.21 17.38L15.41 15.18C15.69 14.9 16.08 14.8 16.45 14.9C17.65 15.3 18.93 15.5 20.25 15.5C20.8 15.5 21.25 15.95 21.25 16.5V20.25C21.25 20.8 20.8 21.25 20.25 21.25C10.19 21.25 2.75 13.81 2.75 3.75C2.75 3.2 3.2 2.75 3.75 2.75H7.5C8.05 2.75 8.5 3.2 8.5 3.75C8.5 5.07 8.7 6.35 9.1 7.55C9.2 7.92 9.1 8.31 8.82 8.59L6.62 10.79Z",
  mapMarkerOutline:
    "M12 2A7 7 0 0 0 5 9C5 14.25 12 22 12 22S19 14.25 19 9A7 7 0 0 0 12 2M12 11.5A2.5 2.5 0 0 1 9.5 9A2.5 2.5 0 0 1 12 6.5A2.5 2.5 0 0 1 14.5 9A2.5 2.5 0 0 1 12 11.5Z",
  alertCircleOutline:
    "M13 13H11V7H13M13 17H11V15H13M12 2A10 10 0 0 0 2 12A10 10 0 0 0 12 22A10 10 0 0 0 22 12A10 10 0 0 0 12 2Z",
  login: "M10 17L12 19L19 12L12 5L10 7L14 11H3V13H14L10 17Z",
  keyOutline:
    "M7 14A2 2 0 0 1 9 12A2 2 0 0 1 11 14A2 2 0 0 1 9 16A2 2 0 0 1 7 14M12.65 10.65L11.5 11.8L13.5 13.8L12.09 15.21L10.09 13.21L8.94 14.36L10.94 16.36L9.53 17.77L5.76 14L7.17 12.59L8.32 13.74L9.47 12.59L7.47 10.59L10.06 8H15V10H12.65Z",
};

const OTP_TTL_SEC = 5 * 60;

function cleanPhone(p = "") {
  return String(p).replace(/\D/g, "").slice(0, 10);
}

export default function VehicleOwnerSignUp() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const params = new URLSearchParams(search);

  const { ownerSignIn, setUser } = useUser();

  // Always land on owner landing dashboard after success
  const effectiveRedirect = "/owner/dashboard";

  const seedPhone = params.get("phoneNumber") || "";
  const via = params.get("via");
  const isSigninCompletionFlow = via === "signin";

  const [step, setStep] = useState("details"); // "details" | "otp"

  const [form, setForm] = useState({
    phoneNumber: "",
    otp: "",
    name: "",
    email: "",
    address: "",
  });

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [addrBusy, setAddrBusy] = useState(false);
  const [addressOpen, setAddressOpen] = useState(false);
  const [activeAddressIndex, setActiveAddressIndex] = useState(-1);
  const debounceRef = useRef(null);
  const lastQueryRef = useRef("");
  const addressInputRef = useRef(null);
  const addressCloseRef = useRef(null);

  const [geoBusy, setGeoBusy] = useState(false);
  const [geoError, setGeoError] = useState("");

  const [otpTtl, setOtpTtl] = useState(OTP_TTL_SEC);

  useEffect(() => {
    if (seedPhone) {
      setForm((p) => ({ ...p, phoneNumber: cleanPhone(seedPhone) }));
    }
  }, [seedPhone]);

  // OTP countdown – only for normal signup (not signin completion)
  useEffect(() => {
    if (isSigninCompletionFlow || step !== "otp") return;
    setOtpTtl(OTP_TTL_SEC);
    const id = setInterval(() => {
      setOtpTtl((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(id);
  }, [step, isSigninCompletionFlow]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (addressCloseRef.current) clearTimeout(addressCloseRef.current);
    };
  }, []);

  const onChange = (key, value) => {
    setError("");
    setGeoError("");
    setForm((p) => ({ ...p, [key]: value }));
  };

  const nameOk = useMemo(
    () => form.name.trim().length > 0,
    [form.name]
  );
  const addressOk = useMemo(
    () => form.address.trim().length >= 3,
    [form.address]
  );
  const normalizedPhone = useMemo(
    () => cleanPhone(form.phoneNumber),
    [form.phoneNumber]
  );

  const canSendOtp = useMemo(
    () => normalizedPhone.length === 10 && nameOk && addressOk,
    [normalizedPhone, nameOk, addressOk]
  );

  const canVerifyOtp = useMemo(
    () =>
      normalizedPhone.length === 10 &&
      nameOk &&
      addressOk &&
      (form.otp || "").trim().length >= 4,
    [normalizedPhone, nameOk, addressOk, form.otp]
  );

  const canCompleteSigninProfile = useMemo(
    () => normalizedPhone.length === 10 && nameOk && addressOk,
    [normalizedPhone, nameOk, addressOk]
  );

  /* ---------------- Address autocomplete ---------------- */

  const fetchAddressSuggestions = async (query) => {
    const trimmed = (query || "").trim();
    lastQueryRef.current = trimmed;

    if (trimmed.length < 3) {
      setAddressSuggestions([]);
      setAddrBusy(false);
      return;
    }

    setAddrBusy(true);
    try {
      const preds = await ownerLocationAutocompleteApi(trimmed);
      if (lastQueryRef.current === trimmed) {
        setAddressSuggestions(preds);
        setAddressOpen(true);
        setActiveAddressIndex(preds.length ? 0 : -1);
      }
    } catch (err) {
      console.error("Owner address autocomplete failed", err);
      setAddressSuggestions([]);
    } finally {
      if (lastQueryRef.current === trimmed) {
        setAddrBusy(false);
      }
    }
  };

  const handleAddressChange = (value) => {
    onChange("address", value);
    setAddressOpen(true);
    setActiveAddressIndex(-1);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchAddressSuggestions(value);
    }, 300);
  };

  const handleSelectAddress = (itemOrText) => {
    const description = typeof itemOrText === "string" ? itemOrText : itemOrText?.fullText || "";
    setGeoError("");
    setAddressSuggestions([]);
    setAddressOpen(false);
    setActiveAddressIndex(-1);
    lastQueryRef.current = description;
    setForm((p) => ({ ...p, address: description }));
  };

  const handleAddressFocus = () => {
    setAddressOpen(true);
    window.requestAnimationFrame(() => {
      addressInputRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    });
  };

  const handleAddressBlur = () => {
    if (addressCloseRef.current) clearTimeout(addressCloseRef.current);
    addressCloseRef.current = setTimeout(() => {
      setAddressOpen(false);
      setActiveAddressIndex(-1);
    }, 140);
  };

  const handleAddressKeyDown = (e) => {
    if (!addressOpen || !addressSuggestions.length) {
      if (e.key === "ArrowDown" && form.address.trim().length >= 3) {
        setAddressOpen(true);
      }
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveAddressIndex((idx) =>
        idx < addressSuggestions.length - 1 ? idx + 1 : 0
      );
      return;
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveAddressIndex((idx) =>
        idx > 0 ? idx - 1 : addressSuggestions.length - 1
      );
      return;
    }

    if (e.key === "Enter" && activeAddressIndex >= 0) {
      e.preventDefault();
      handleSelectAddress(addressSuggestions[activeAddressIndex]);
      return;
    }

    if (e.key === "Escape") {
      setAddressOpen(false);
      setActiveAddressIndex(-1);
    }
  };

  const handleUseCurrentLocation = () => {
    if (geoBusy) return;
    setGeoError("");
    setAddressSuggestions([]);
    setAddrBusy(false);

    if (!("geolocation" in navigator)) {
      setGeoError("Location is not supported on this device/browser.");
      return;
    }

    setGeoBusy(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;

        try {
          const url =
            `https://nominatim.openstreetmap.org/reverse?format=jsonv2` +
            `&lat=${encodeURIComponent(latitude)}` +
            `&lon=${encodeURIComponent(longitude)}`;

          const res = await fetch(url, {
            headers: { Accept: "application/json" },
          });

          let finalAddress = "";
          if (res.ok) {
            const json = await res.json();
            finalAddress = json?.display_name || "";
          }

          if (!finalAddress) {
            finalAddress = `Current location (${latitude.toFixed(
              5
            )}, ${longitude.toFixed(5)})`;
          }

          setForm((p) => ({ ...p, address: finalAddress }));
          lastQueryRef.current = finalAddress;
          setAddressSuggestions([]);
        } catch (e) {
          console.warn("Reverse geocode failed", e);
          const fallback = `Current location (${latitude.toFixed(
            5
          )}, ${longitude.toFixed(5)})`;
          setForm((p) => ({ ...p, address: fallback }));
          lastQueryRef.current = fallback;
          setAddressSuggestions([]);
        } finally {
          setGeoBusy(false);
        }
      },
      (err) => {
        console.error("Geolocation error", err);
        if (err?.code === 1) {
          setGeoError("Location permission denied. Please allow access.");
        } else if (err?.code === 2) {
          setGeoError("Could not detect your location. Try again.");
        } else if (err?.code === 3) {
          setGeoError("Location request timed out. Try again.");
        } else {
          setGeoError("Failed to fetch current location.");
        }
        setGeoBusy(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 12000,
        maximumAge: 30000,
      }
    );
  };

  /* ---------------- Actions ---------------- */

  // Normal signup – Send OTP
  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (isSigninCompletionFlow || !canSendOtp || busy) return;
    setBusy(true);
    setError("");

    try {
      await ownerRequestOtpApi({ phoneNumber: normalizedPhone });
      setStep("otp");
    } catch (err) {
      console.error("Owner OTP request failed", err);
      setError("Could not send OTP. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  // Normal signup – Verify OTP + sign-in
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (isSigninCompletionFlow || !canVerifyOtp || busy) return;
    setBusy(true);
    setError("");

    try {
      await ownerSignIn({
        mode: "otp",
        phoneNumber: normalizedPhone,
        otp: (form.otp || "").trim(),
        name: form.name.trim(),
        address: form.address.trim(),
        email: form.email.trim() || undefined,
      });

      navigate(effectiveRedirect, { replace: true });
    } catch (err) {
      console.error("Owner OTP verify/login failed", err);
      setError("Invalid or expired OTP. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  // ✅ Sign-in completion flow – profile complete only
  const handleCompleteFromSignin = async (e) => {
    e.preventDefault();
    if (!isSigninCompletionFlow || !canCompleteSigninProfile || busy) return;
    setBusy(true);
    setError("");

    try {
      const email = form.email.trim();
      const payload = {
        name: form.name.trim(),
        address: form.address.trim(),
      };
      if (email) payload.email = email;

      const owner = await ownerCompleteProfileApi(payload);

      // 🔑 Ensure we persist phoneNumber so isAuthenticated becomes true
      const phoneNumber = normalizedPhone;

      const nextUser = {
        email: owner?.email || email || "",
        userName: owner?.name || payload.name || null,
        role: ROLES.OWNER,
        phoneNumber,
      };

      // token is already stored at OTP sign-in step; now we just set the user snapshot
      setUser(nextUser);

      // keep ownerEmail in sync for Header’s owner surface logic
      if (nextUser.email) {
        try {
          localStorage.setItem("ownerEmail", nextUser.email);
        } catch {
          // ignore storage errors
        }
      }

      navigate(effectiveRedirect, { replace: true });
    } catch (err) {
      console.error("Owner profile completion failed", err);
      setError(
        "Could not complete registration. Please sign in again and retry."
      );
    } finally {
      setBusy(false);
    }
  };

  const fmtTtl = (sec) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    const ss = s < 10 ? `0${s}` : String(s);
    return `${m}:${ss}`;
  };

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <Header />

      <main className="mx-auto max-w-[560px] px-4 py-12 pb-28 sm:px-6 xl:px-8">
        <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
          {/* Header */}
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-50 border border-slate-200">
              <Icon
                d={mdi.steering}
                size={20}
                className="text-slate-600"
                title="Vehicle owner sign up"
              />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Register as vehicle owner</h1>
              <p className="text-slate-600 text-sm">
                Add your owner details to complete registration and manage your
                fleet on Wayroot.
              </p>
            </div>
          </div>

          {/* Banners */}
          {error && (
            <div className="mt-4 flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              <Icon d={mdi.alertCircleOutline} size={18} />
              <span>{error}</span>
            </div>
          )}

          {isSigninCompletionFlow && !error && (
            <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-xs text-emerald-700">
              OTP was already verified during sign in. Please confirm your
              details to complete owner registration.
            </div>
          )}

          {/* Step indicator */}
          {!isSigninCompletionFlow && (
            <div className="mt-4 flex items-center justify-between text-[11px]">
              <span className="font-semibold text-slate-500">
                Step {step === "details" ? "1" : "2"} of 2
              </span>
              {step === "otp" && (
                <button
                  type="button"
                  onClick={() => setStep("details")}
                  className="inline-flex items-center gap-1 text-slate-600 font-semibold"
                >
                  <span className="text-xs">←</span>
                  <span>Edit details</span>
                </button>
              )}
            </div>
          )}

          {/* Form */}
          <form
            className="mt-6 space-y-4"
            onSubmit={
              isSigninCompletionFlow
                ? handleCompleteFromSignin
                : step === "details"
                ? handleSendOtp
                : handleVerifyOtp
            }
          >
            {/* Phone */}
            <label className="block">
              <span className="text-sm text-slate-600">Phone number</span>
              <div className="mt-1.5 relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                  <Icon d={mdi.phoneOutline} />
                </span>
                <input
                  type="tel"
                  name="phoneNumber"
                  inputMode="numeric"
                  placeholder="10-digit mobile number"
                  value={form.phoneNumber}
                  onChange={(e) =>
                    onChange("phoneNumber", cleanPhone(e.target.value))
                  }
                  required
                  disabled={isSigninCompletionFlow}
                  className={`w-full rounded-xl border border-slate-300 bg-white px-10 py-2.5 text-[15px] outline-none focus:ring-2 focus:ring-slate-900/10 ${
                    isSigninCompletionFlow ? "opacity-70 cursor-not-allowed" : ""
                  }`}
                />
              </div>
            </label>

            {/* Name */}
            <label className="block">
              <span className="text-sm text-slate-600">Owner name</span>
              <div className="mt-1.5 relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                  <Icon d={mdi.accountOutline} />
                </span>
                <input
                  type="text"
                  name="name"
                  placeholder="Your full name"
                  value={form.name}
                  onChange={(e) => onChange("name", e.target.value)}
                  required
                  className={`w-full rounded-xl border ${
                    !nameOk && form.name
                      ? "border-rose-300 bg-rose-50/40"
                      : "border-slate-300 bg-white"
                  } px-10 py-2.5 text-[15px] outline-none focus:ring-2 focus:ring-slate-900/10`}
                />
              </div>
              {!nameOk && form.name && (
                <p className="mt-1 text-xs text-rose-600">
                  Owner name is required.
                </p>
              )}
            </label>

            {/* Email optional */}
            <label className="block">
              <span className="text-sm text-slate-600">Email (optional)</span>
              <div className="mt-1.5 relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                  <Icon d={mdi.emailOutline} />
                </span>
                <input
                  type="email"
                  name="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="owner@example.com"
                  value={form.email}
                  onChange={(e) => onChange("email", e.target.value)}
                  className="w-full rounded-xl border border-slate-300 bg-white px-10 py-2.5 text-[15px] outline-none focus:ring-2 focus:ring-slate-900/10"
                />
              </div>
            </label>

            {/* Address + suggestions + GPS */}
            <label className="block">
              <span className="text-sm text-slate-600">Base location</span>
              <div className="mt-1.5 flex items-stretch gap-2">
                {/* Input + suggestions */}
                <div className="relative flex-1">
                  <span className="absolute left-3 top-3.5 text-slate-400">
                    <Icon d={mdi.mapMarkerOutline} />
                  </span>
                  <input
                    type="text"
                    ref={addressInputRef}
                    name="address"
                    placeholder="Search area, city, or landmark"
                    value={form.address}
                    onChange={(e) => handleAddressChange(e.target.value)}
                    onFocus={handleAddressFocus}
                    onBlur={handleAddressBlur}
                    onKeyDown={handleAddressKeyDown}
                    required
                    autoComplete="off"
                    className={`w-full rounded-xl border ${
                      (!addressOk && form.address) || geoError
                        ? "border-rose-300 bg-rose-50/40"
                      : "border-slate-300 bg-white"
                    } pl-10 pr-3 py-2.5 text-[15px] outline-none focus:ring-2 focus:ring-slate-900/10`}
                  />

                  {addressOpen && form.address.trim().length >= 3 && (
                    <div
                      className="absolute z-20 mt-2 w-full overflow-auto rounded-xl border border-slate-200 bg-white shadow-lg"
                      style={{ maxHeight: "min(18rem, calc(100vh - 220px))" }}
                    >
                      {addrBusy && (
                        <div className="px-3 py-2 text-xs text-slate-500">
                          Searching…
                        </div>
                      )}

                      {!addrBusy && addressSuggestions.length === 0 && (
                        <div className="px-3 py-2 text-xs text-slate-500">
                          No suggestions
                        </div>
                      )}

                      {!addrBusy &&
                        addressSuggestions.map((p, idx) => (
                          <button
                            type="button"
                            key={p.placeId || p.id || idx}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handleSelectAddress(p)}
                            className={`w-full px-3 py-2 text-left text-sm ${
                              idx === activeAddressIndex
                                ? "bg-slate-50"
                                : "hover:bg-slate-50"
                            }`}
                          >
                            <div className="text-sm font-medium text-slate-800">{p.primaryText || p.fullText}</div>
                            {p.secondaryText ? <div className="mt-0.5 text-xs text-slate-500">{p.secondaryText}</div> : null}
                          </button>
                        ))}
                    </div>
                  )}
                </div>

                {/* GPS button */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleUseCurrentLocation();
                  }}
                  disabled={geoBusy}
                  className="shrink-0 inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 text-[11px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                  style={{ minHeight: 42 }}
                  title="Use current location"
                >
                  <span className="text-xs">📍</span>
                  <span className="whitespace-nowrap">
                    {geoBusy ? "Locating…" : "Use Current Location"}
                  </span>
                </button>
              </div>

              {!addressOk && form.address && !geoError && (
                <p className="mt-1 text-xs text-rose-600">
                  Address is required.
                </p>
              )}
              {geoError && (
                <p className="mt-1 text-xs text-rose-600">{geoError}</p>
              )}
              {!geoError && !form.address && (
                <p className="mt-1 text-xs text-slate-500">
                  Select the vendor base location from suggestions or tap{" "}
                  <span className="font-medium">Use Current Location</span>.
                </p>
              )}
            </label>

            {/* OTP step (normal signup only) */}
            {!isSigninCompletionFlow && step === "otp" && (
              <>
                <label className="block">
                  <span className="text-sm text-slate-600">Enter OTP</span>
                  <div className="mt-1.5 relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                      <Icon d={mdi.keyOutline} />
                    </span>
                    <input
                      type="tel"
                      inputMode="numeric"
                      name="otp"
                      placeholder="4-6 digit OTP"
                      value={form.otp}
                      onChange={(e) =>
                        onChange(
                          "otp",
                          e.target.value.replace(/\D/g, "")
                        )
                      }
                      className="w-full rounded-xl border border-slate-300 bg-white px-10 py-2.5 text-[15px] outline-none focus:ring-2 focus:ring-slate-900/10"
                    />
                  </div>
                </label>

                <div className="flex items-center justify-between text-[11px] text-slate-500">
                  <span>OTP validity: about 5 minutes</span>
                  <span className="font-semibold text-slate-700">
                    {otpTtl > 0 ? `Time left ${fmtTtl(otpTtl)}` : "OTP expired"}
                  </span>
                </div>
              </>
            )}

            {/* CTA button */}
            <button
              type="submit"
              disabled={
                busy ||
                (isSigninCompletionFlow
                  ? !canCompleteSigninProfile
                  : step === "details"
                  ? !canSendOtp
                  : !canVerifyOtp)
              }
              className="w-full inline-flex items-center justify-center rounded-xl bg-black px-5 py-2.5 text-white font-medium hover:opacity-90 active:opacity-80 disabled:opacity-60 mt-2"
            >
              <span className="inline-flex items-center gap-2">
                <Icon d={mdi.login} size={18} />
                <span>
                  {busy
                    ? isSigninCompletionFlow
                      ? "Saving..."
                      : step === "details"
                      ? "Sending OTP..."
                      : "Verifying..."
                    : isSigninCompletionFlow
                    ? "Complete registration"
                    : step === "details"
                    ? "Send OTP"
                    : "Verify & continue"}
                </span>
              </span>
            </button>

            {/* Resend OTP row */}
            {!isSigninCompletionFlow && step === "otp" && (
              <div className="mt-3 flex items-center justify-between text-[11px]">
                <span className="text-slate-500">
                  Didn&apos;t receive the OTP?
                </span>
                <button
                  type="button"
                  onClick={handleSendOtp}
                  disabled={busy || !canSendOtp}
                  className={`font-semibold ${
                    busy || !canSendOtp
                      ? "text-slate-300 cursor-not-allowed"
                      : "text-pink-600"
                  }`}
                >
                  Resend OTP
                </button>
              </div>
            )}
          </form>

          {/* Existing owner link */}
          <div className="mt-6 text-sm text-slate-700">
            Already a vehicle owner?{" "}
            <Link
              to={`/vehicle-owner/vehicle-owner-signin?redirect=${encodeURIComponent(
                effectiveRedirect
              )}`}
              className="underline underline-offset-4 hover:no-underline"
            >
              Sign in here
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
